import { resolveMessageReplies, mergeOptimisticReactions } from './message-contract';
import {
  bottomNavigationBarVisibleAtom,
  chatTutorialVariantAtom,
  currentTutorialAtom,
  optimisticChannelsReadStatusAtom,
  visibleChannelAtom,
} from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { ListingList } from 'app/components/listing-list';
import { type ListingOfferCardProps } from 'app/components/listing-offer-card';
import { ShowErrors } from 'app/components/show-errors';
import { InsuredUser } from 'app/components/insured-users';
import { TopLoadingIndicator, useTopLoadingIndicator } from 'app/components/top-loading-indicator';
import { useTutorials } from 'app/components/tutorial-overlay';
import UnavailabilityBanner from 'app/components/unavailability-banner';
import { ChatBubble } from 'app/design/icons/chat-bubble';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Accessory } from 'app/design/primitives/page/accessory';
import { ListPage, type FlashOrFlatList } from 'app/design/primitives/page/list';
import {
  KeyboardResponsiveView,
  useKeyboardResponsiveViewKeyboardVisible,
} from 'app/design/primitives/keyboard-responsive-view';
import { Text } from 'app/design/primitives/text';

import { useNavigationOptions } from 'app/hooks/navigation';
import { useIsCurrentlyUnavailable } from 'app/hooks/use-is-currently-unavailable';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { localeAwareFormat } from 'app/lib/date';
import { isLender } from 'app/lib/user';
import { useGetInterlocutor, useIsAuthor } from 'app/network/chat';
import {
  useBatchUnreadChannels,
  NO_CHANNEL_SELECTED,
  useEditMessage,
  useMarkChannelAsRead,
  useOneChannel,
  useToggleReaction,
  useUpdateChannelSilenced,
  useLeaveGroupConversation,
  useLinkOfferToChannel,
  useUpdateGroupConversation,
} from 'app/network/chat/channel';
import { MESSAGE_ID_LOADING } from 'app/network/chat/message';
import { useMyProfile } from 'app/network/profile';
import { useAllListings } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { AllChannelsScreenComponent } from 'app/screens/chat/all-channels/components/all-channels';
import { navigateToAllChannels } from 'app/screens/chat/navigator-helper';
import { SilenceConversationBottomSheet } from 'app/screens/chat/components/silence-conversation-bottom-sheet';
import { LoanFeeModals, useLoanFeeModals } from 'app/screens/management/loan-fee';
import { useOnFocus } from 'app/services/on-focus';
import type { ChatUser, LoanMessage, Message as MessageType, TextMessageMessage } from 'app/types/chat';
import {
  ChatConversationMemberRole,
  CustomChannelType,
  CustomMessageType,
  MessageKind,
  isChannelSilenced,
  isSystemMessage,
  isUserMessage,
} from 'app/types/chat';
import { ListingType } from 'app/types/listing';
import { LoanStatus } from 'app/types/loan';
import { getSupersededLoanIds } from './loan-proposals';
import type { SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { Tutorial } from 'app/types/tutorial';
import { DynamicFeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { isMobileNative, usePageTitle, useUISize } from 'app/utils/web-helpers';
import { differenceInMinutes, isToday, isYesterday } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import type { TFunction } from 'i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { getDefaultHeaderHeight } from '@react-navigation/elements';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  ActivityIndicator,
  AppState,
  Keyboard,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
  useWindowDimensions,
} from 'react-native';
import { Modal } from 'app/design/primitives/modal';
import { Banner } from './components/banner';
import { ChannelBackButton, ChannelTitle, ChannelTitleMenuButton } from './components/channel-title';
import { GroupAvatarStack } from 'app/screens/chat/components/group-avatar-stack';
import { PCImage } from 'app/design/primitives/image';
import { Message } from './components/message';
import { ChannelToolbar, useChannelToolbar } from './components/toolbar';
import { EditPreview, ReplyPreview } from './components/reply-preview';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { Clubs } from 'app/design/icons/clubs';
import { Radio } from 'app/design/primitives/radio';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { canUseOfferHub } from 'app/screens/intro/navigator-helper';
import { HowItWorksInlineCard } from './components/how-it-works-card';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { createTheme as createDSTheme } from 'app/shopify';
import { images } from 'app/design/images';
import { IconChevronDown, IconHelpCircle, IconPlus, IconUser, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useSupportReplyWait } from './use-support-reply-wait';
import { SupportReplyTypingBubble } from './components/support-reply-typing-bubble';
import { isTransportSupportChannel } from 'app/screens/chat/components/transport-support-avatar';
const theme = createDSTheme();
const EMPTY_OPTIMISTIC_REACTIONS = new Map<string, string[]>();

function CompactLinkOfferCard({ listing: offer, selectedRowsState }: ListingOfferCardProps) {
  const [selectedOfferIds = [], setSelectedOfferIds] = selectedRowsState ?? [];
  const isSelected = selectedOfferIds.includes(offer.id);
  const subtitle = offer.description;
  const selectOffer = () => setSelectedOfferIds?.([offer.id]);

  return (
    <Pressable onPress={selectOffer}>
      <Row
        alignItems="center"
        gap="spacing.general.x3"
        padding="spacing.general.x2"
        style={{
          opacity: selectedOfferIds.length > 0 ? (isSelected ? 1 : 0.65) : 1,
          borderRadius: theme.borderRadii['radius.sm'],
          borderWidth: 1,
          borderColor: isSelected ? theme.colors['border.secondary'] : theme.colors['border.disabled'],
          backgroundColor: isSelected ? theme.colors['surface.selected'] : theme.colors.white,
        }}
      >
        <ListingThumbnail
          before={offer.before}
          isFound={Boolean(offer.lender)}
          picture={offer.picture}
          size={48}
          subtype={offer.subtype}
          type={offer.type}
        />
        <Column flex={1} gap="spacing.general.x0,5" minWidth={0}>
          <Text flexShrink={1} fontWeight="700" numberOfLines={2}>
            {offer.item.name}
          </Text>
          {subtitle ? (
            <Text color="text.secondary" numberOfLines={1}>
              {subtitle}
            </Text>
          ) : null}
        </Column>
        <Radio checked={isSelected} width={isSelected ? 25 : 20} />
      </Row>
    </Pressable>
  );
}

function ChannelAccessory({ children }: { children: React.ReactNode }) {
  const keyboardVisible = useKeyboardResponsiveViewKeyboardVisible();
  return (
    <Accessory elevated marginBottom={keyboardVisible ? 0 : undefined}>
      {children}
    </Accessory>
  );
}

function ChannelHeaderLayout({ children }: { children: React.ReactNode }) {
  if (Platform.OS === 'web') return <>{children}</>;
  return <View style={channelLayoutStyles.header}>{children}</View>;
}

const CAFÉ_DES_HABITUDES_ID = '52051c9c-7cd4-46d8-a489-f0ac6c269349';

type Channel = NonNullable<ReturnType<typeof useOneChannel>['data']>;

function formatMessageSeparator(date: Date | number | string, t: TFunction<'one-channel'>) {
  const parsedDate = typeof date === 'string' ? new Date(date) : date;
  const time = localeAwareFormat(parsedDate, 'p');
  if (isToday(parsedDate)) return t('messageDateSeparator.todayAt', { time });
  if (isYesterday(parsedDate)) return t('messageDateSeparator.yesterdayAt', { time });
  return t('messageDateSeparator.dateAt', {
    date: localeAwareFormat(parsedDate, t('dateFormat_MMMdYYYY', { ns: 'common' })),
    time,
  });
}

function buildChannelMessages({
  channel,
  channelUrl,
  currentUserId,
  hasCurrentUser,
  messagePrompt,
  promptCreatedAt,
}: {
  channel: Channel | undefined;
  channelUrl: string;
  currentUserId: string | undefined;
  hasCurrentUser: boolean;
  messagePrompt: TextMessageMessage | undefined;
  promptCreatedAt: number;
}) {
  const messages =
    resolveMessageReplies(channel?.messages ?? []).filter(
      (message) =>
        message.type !== MessageKind.Message ||
        (message.message !== ' ... ' && JSON.stringify(message.message) !== JSON.stringify(messagePrompt)),
    ) ?? [];
  if (!messagePrompt) return messages;
  const otherUser =
    channel?.members.find((member) => member.user_id !== currentUserId) ??
    ({
      firstname: 'Partage Club',
      nickname: 'Partage club',
      profile_url: '0',
      user_id: '0',
      isSupport: false,
    } satisfies ChatUser);
  const promptMessage = {
    channel_url: channel?.channel_url ?? channelUrl,
    type: MessageKind.Message,
    message: channel && hasCurrentUser ? messagePrompt : ' ... ',
    user: otherUser,
    created_at: promptCreatedAt,
    message_id: `prompt-${channelUrl}-${String(promptCreatedAt)}`,
    viewed: false,
  } satisfies MessageType;
  const firstOlderMessageIndex = messages.findIndex((message) => message.created_at <= promptCreatedAt);
  messages.splice(firstOlderMessageIndex < 0 ? messages.length : firstOlderMessageIndex, 0, promptMessage);
  return messages;
}

function usePreparedMessages({
  channel,
  channelUrl,
  currentUserId,
  hasCurrentUser,
  messagePrompt,
}: {
  channel: Channel | undefined;
  channelUrl: string;
  currentUserId: string | undefined;
  hasCurrentUser: boolean;
  messagePrompt: TextMessageMessage | undefined;
}) {
  const channelMessages = channel?.messages;
  const [promptCreatedAt] = React.useReducer((value: number) => value, 0, Date.now);
  const messages = buildChannelMessages({
    channel,
    channelUrl,
    currentUserId,
    hasCurrentUser,
    messagePrompt,
    promptCreatedAt,
  });
  const [pending, setPending] = useState<{
    source: MessageType[] | undefined;
    value: MessageType | undefined;
  }>({ source: channelMessages, value: undefined });
  const currentPending = pending.source === channelMessages ? pending : { source: channelMessages, value: undefined };
  if (pending !== currentPending) setPending(currentPending);
  const setMessageBeingSent: React.Dispatch<React.SetStateAction<MessageType | undefined>> = (nextValue) => {
    setPending((previous) => {
      const currentValue = previous.source === channelMessages ? previous.value : undefined;
      return {
        source: channelMessages,
        value: typeof nextValue === 'function' ? nextValue(currentValue) : nextValue,
      };
    });
  };
  const allMessages = [...(currentPending.value ? [currentPending.value] : []), ...messages];
  return { allMessages, messages, setMessageBeingSent };
}

function getLoanSummary(channelMessages: MessageType[] | undefined) {
  const loans = channelMessages
    ?.filter(
      (message): message is LoanMessage =>
        message.type === MessageKind.Message && message.custom_type === CustomMessageType.Loan,
    )
    .map((message) => message.loan);
  return {
    acceptedLoan: [...(loans ?? [])]
      .reverse()
      .find((loan) => loan.status === LoanStatus.Accepted || loan.status === LoanStatus.Returned),
    completedLoan: loans?.find((loan) => loan.status === LoanStatus.Completed),
    supersededLoanIds: getSupersededLoanIds(loans ?? []),
  };
}

function getSentMessageSummary(allMessages: MessageType[], profileUserId: string | undefined) {
  const viewedMessagesSent = allMessages.filter(
    (message) =>
      message.viewed &&
      !message.is_invisible &&
      Boolean(profileUserId) &&
      message.user?.user_id === profileUserId &&
      (message.type !== MessageKind.Message ||
        (message.custom_type !== CustomMessageType.RecomPrompt &&
          message.custom_type !== CustomMessageType.LatePrompt &&
          message.custom_type !== CustomMessageType.ReturnedPrompt)),
  );
  const textMessagesSent = allMessages.filter(
    (message) =>
      message.type === MessageKind.Message &&
      !message.custom_type &&
      Boolean(profileUserId) &&
      message.user?.user_id === profileUserId,
  );
  const loanRequestsSent = allMessages.filter(
    (message) => message.type === MessageKind.Message && message.custom_type === CustomMessageType.Loan,
  );
  return {
    lastViewedMessageSent: viewedMessagesSent[0],
    loanRequestsSent,
    sentNoTextMessages: textMessagesSent.length === 0,
    sentOneTextMessage: textMessagesSent.length === 1,
  };
}

function getPromptMessageText(currentUserId: string | undefined, interlocutorFirstName: string | undefined) {
  if (currentUserId !== CAFÉ_DES_HABITUDES_ID) return undefined;
  return `Bonjour ${interlocutorFirstName ?? ''}!\n\nVous pouvez venir chercher l’objet quand vous voulez à ces dates précises dans les heures d’ouverture du Café des Habitudes.\n\nVous avez un 50% sur votre boisson chaude lors de votre venue - gracieuseté de Partage Club !`;
}

function useChannelLifecycle(channelUrl: string, isFocused: boolean) {
  const query = useOneChannel(channelUrl);
  const { refetch } = query;
  const channel = query.data ?? undefined;
  const loadedChannelUrl = channel?.channel_url;
  const unreadMessageCount = channel?.unread_message_count ?? 0;
  const setChannelsReadStatus = useSetAtom(optimisticChannelsReadStatusAtom);
  const { mutate: markChannelAsRead } = useMarkChannelAsRead({
    onSuccess: (_data, readChannelUrl) => {
      setChannelsReadStatus((previous) =>
        Object.fromEntries(Object.entries(previous).filter(([entryChannelUrl]) => entryChannelUrl !== readChannelUrl)),
      );
    },
    onError: (_error, readChannelUrl) => {
      setChannelsReadStatus((previous) => ({ ...previous, [readChannelUrl]: false }));
    },
  });
  const [appState, setAppState] = useState(AppState.currentState);
  const [manuallyUnreadUrl, setManuallyUnreadUrl] = useState<string>();
  const { mutate: markChannelAsUnread } = useBatchUnreadChannels({
    onError: () => setManuallyUnreadUrl(undefined),
  });
  const setVisibleChannel = useSetAtom(visibleChannelAtom);

  useRegisterLoading(query.isPending, 'OneChannelScreen');
  useEffect(() => {
    const timer = setInterval(() => void refetch(), 60 * 1000);
    return () => clearInterval(timer);
  }, [refetch]);
  useEffect(() => {
    const subscription = AppState.addEventListener('change', setAppState);
    return () => subscription.remove();
  }, []);
  useFocusEffect(() => () => setManuallyUnreadUrl(undefined));
  useEffect(() => {
    const shouldMarkRead =
      isFocused &&
      loadedChannelUrl &&
      unreadMessageCount > 0 &&
      appState === 'active' &&
      manuallyUnreadUrl !== loadedChannelUrl;
    if (shouldMarkRead) markChannelAsRead(loadedChannelUrl);
  }, [appState, isFocused, loadedChannelUrl, manuallyUnreadUrl, markChannelAsRead, unreadMessageCount]);
  useOnFocus(() => void refetch());
  useEffect(() => {
    setVisibleChannel(channelUrl === NO_CHANNEL_SELECTED ? undefined : (loadedChannelUrl ?? channelUrl));
    return () => setVisibleChannel(undefined);
  }, [channelUrl, loadedChannelUrl, setVisibleChannel]);

  const markAsUnread = () => {
    if (!loadedChannelUrl) return;
    setManuallyUnreadUrl(loadedChannelUrl);
    markChannelAsUnread([loadedChannelUrl]);
  };
  return { appState, channel, loadedChannelUrl, markAsUnread, query, refetch };
}

function canLinkOfferToChannel({
  acceptedLoan,
  channel,
  isUserPending,
  linkedOffer,
  profileUserId,
  userFeatureFlags,
}: {
  acceptedLoan: unknown;
  channel: Channel | undefined;
  isUserPending: boolean;
  linkedOffer: unknown;
  profileUserId: string | undefined;
  userFeatureFlags: NonNullable<ReturnType<typeof useUser>['data']>['featureFlags'];
}) {
  const listing = channel?.listing;
  return Boolean(
    !isUserPending &&
    isFeatureEnabled(DynamicFeatureFlag.LinkOfferToRequestChat, userFeatureFlags) &&
    channel?.custom_type === CustomChannelType.Listing &&
    listing?.type === ListingType.request &&
    (channel.canChangeLinkedOffer ?? !linkedOffer) &&
    !acceptedLoan &&
    !listing.lender &&
    profileUserId &&
    listing.userId !== profileUserId,
  );
}

interface LinkOfferState {
  context: string;
  searchQuery: string;
  selectedIds: string[];
  inline: boolean;
  modal: boolean;
}

function resolveStateAction<T>(action: React.SetStateAction<T>, currentValue: T) {
  return typeof action === 'function' ? (action as (value: T) => T)(currentValue) : action;
}

function getCurrentLinkOfferState(state: LinkOfferState, context: string, itemName: string) {
  return state.context === context
    ? state
    : { context, searchQuery: itemName, selectedIds: [], inline: false, modal: false };
}

function useLinkOfferState(context: string, itemName: string) {
  const [state, setState] = useState<LinkOfferState>({
    context,
    searchQuery: itemName,
    selectedIds: [],
    inline: false,
    modal: false,
  });
  const current = getCurrentLinkOfferState(state, context, itemName);
  if (state !== current) setState(current);
  const update = useCallback(
    (values: Partial<LinkOfferState>) => {
      setState((previous) => ({ ...getCurrentLinkOfferState(previous, context, itemName), ...values, context }));
    },
    [context, itemName],
  );
  const setSearchQuery = (value: React.SetStateAction<string>) => {
    setState((previous) => {
      const currentState = getCurrentLinkOfferState(previous, context, itemName);
      return {
        ...currentState,
        context,
        searchQuery: resolveStateAction(value, currentState.searchQuery),
        selectedIds: [],
      };
    });
  };
  const setSelectedIds = useCallback(
    (value: React.SetStateAction<string[]>) => {
      setState((previous) => {
        const currentState = getCurrentLinkOfferState(previous, context, itemName);
        const selectedIds = resolveStateAction(value, currentState.selectedIds);
        if (currentState === previous && selectedIds === currentState.selectedIds) return previous;
        return { ...currentState, context, selectedIds };
      });
    },
    [context, itemName],
  );
  return { current, setSearchQuery, setSelectedIds, update };
}

function getLinkOfferVisibility({
  canLinkOffer,
  inlineRequested,
  isFetching,
  modalRequested,
  topOfferCount,
}: {
  canLinkOffer: boolean;
  inlineRequested: boolean;
  isFetching: boolean;
  modalRequested: boolean;
  topOfferCount: number;
}) {
  const inline = canLinkOffer && inlineRequested;
  const modal = canLinkOffer && modalRequested;
  const promoteToModal = inline && !isFetching && topOfferCount === 0;
  return { inline: inline && !promoteToModal, modal: modal || promoteToModal, promoteToModal };
}

function useLinkOfferController({
  acceptedLoan,
  channel,
  isUserPending,
  profileUserId,
  refetchChannel,
  userFeatureFlags,
}: {
  acceptedLoan: unknown;
  channel: Channel | undefined;
  isUserPending: boolean;
  profileUserId: string | undefined;
  refetchChannel: ReturnType<typeof useOneChannel>['refetch'];
  userFeatureFlags: NonNullable<ReturnType<typeof useUser>['data']>['featureFlags'];
}) {
  const itemName = channel?.listing?.item.name ?? '';
  const loadedChannelUrl = channel?.channel_url;
  const linkedOffer = channel?.custom_type === CustomChannelType.Listing ? channel.linkedOffer : undefined;
  const canLinkOffer = canLinkOfferToChannel({
    acceptedLoan,
    channel,
    isUserPending,
    linkedOffer,
    profileUserId,
    userFeatureFlags,
  });
  const state = useLinkOfferState(`${loadedChannelUrl ?? ''}\u0000${itemName}\u0000${String(canLinkOffer)}`, itemName);
  const filters = {
    userId: profileUserId,
    type: ListingType.offer,
    take: 100,
    isLoggedIn: true,
    search: state.current.searchQuery.trim(),
    includeCounts: true,
  };
  const query = useAllListings(filters, 'chat-link-offer', {
    enabled:
      canLinkOffer &&
      Boolean(profileUserId) &&
      (state.current.modal || state.current.inline || state.current.searchQuery.trim().length > 0),
  });
  const listings =
    query.data?.pages.flatMap((page) =>
      page.results.filter((offer) => !['archived', 'deleted', 'expired'].includes(offer.status)),
    ) ?? [];
  const topListings = listings.slice(0, 3);
  const visibility = getLinkOfferVisibility({
    canLinkOffer,
    inlineRequested: state.current.inline,
    isFetching: query.isFetching,
    modalRequested: state.current.modal,
    topOfferCount: topListings.length,
  });
  const updateOfferState = state.update;
  useEffect(() => {
    // Once opened, the picker must survive loading and results for subsequent searches.
    if (visibility.promoteToModal) updateOfferState({ inline: false, modal: true });
  }, [updateOfferState, visibility.promoteToModal]);
  const selectedOfferId = state.current.selectedIds[0];
  const selectedOffer = listings.find((offer) => offer.id === selectedOfferId);
  const closeModal = () => state.update({ inline: false, modal: false });
  const openModal = () => state.update({ inline: false, modal: true });
  const onLinkOffer = () => {
    if (!canLinkOffer) return;
    Keyboard.dismiss();
    state.setSearchQuery(itemName);
    if (query.isFetching || topListings.length > 0) {
      state.update({ inline: true });
      return;
    }
    openModal();
  };
  const {
    mutate: linkOfferToChannel,
    isPending: isLinkingOffer,
    error,
  } = useLinkOfferToChannel({
    onSuccess: () => {
      state.setSelectedIds([]);
      closeModal();
      void refetchChannel();
    },
  });
  const unlink = () => {
    if (!canLinkOffer || !linkedOffer || !loadedChannelUrl || isLinkingOffer) return;
    linkOfferToChannel({ channelUrl: loadedChannelUrl, offerId: null });
  };
  const confirm = () => {
    if (!canLinkOffer || !selectedOfferId || !loadedChannelUrl) return;
    linkOfferToChannel({ channelUrl: loadedChannelUrl, offerId: selectedOfferId });
  };
  return {
    canLinkOffer: canLinkOffer && !isLinkingOffer,
    unlink,
    closeModal,
    confirm,
    error,
    filters,
    isLinkingOffer,
    itemName,
    linkedOffer,
    listings,
    onLinkOffer,
    openModal,
    query,
    searchQuery: state.current.searchQuery,
    selectedOffer,
    selectedOfferIds: state.current.selectedIds,
    setInlineOpen: (open: boolean) => state.update({ inline: open }),
    setSearchQuery: state.setSearchQuery,
    setSelectedOfferIds: state.setSelectedIds,
    showInline: visibility.inline,
    showModal: visibility.modal,
    topListings,
  };
}

function getTutorialVisibility({
  channelMessagesLoaded,
  currentTutorial,
  firstMessageTutorialComplete,
  introTutorialComplete,
  isOwnListing,
  listing,
  loanRequestCount,
  sentNoTextMessages,
  sentOneTextMessage,
  tutorial1Shown,
  tutorial2Shown,
}: {
  channelMessagesLoaded: boolean;
  currentTutorial: Tutorial | undefined;
  firstMessageTutorialComplete: boolean;
  introTutorialComplete: boolean;
  isOwnListing: boolean | undefined;
  listing: Channel['listing'];
  loanRequestCount: number;
  sentNoTextMessages: boolean;
  sentOneTextMessage: boolean;
  tutorial1Shown: boolean;
  tutorial2Shown: boolean;
}) {
  const validIntroListing = listing?.type === ListingType.offer || listing?.type === ListingType.request;
  return {
    intro:
      validIntroListing &&
      !isOwnListing &&
      !tutorial1Shown &&
      channelMessagesLoaded &&
      !introTutorialComplete &&
      sentNoTextMessages,
    firstMessage:
      Boolean(listing?.id) &&
      !tutorial2Shown &&
      !firstMessageTutorialComplete &&
      sentOneTextMessage &&
      !currentTutorial &&
      loanRequestCount === 0,
  };
}

function useChannelTutorialState({
  channelMessagesLoaded,
  isOwnListing,
  listing,
  loanRequestCount,
  sentNoTextMessages,
  sentOneTextMessage,
}: {
  channelMessagesLoaded: boolean;
  isOwnListing: boolean | undefined;
  listing: Channel['listing'];
  loanRequestCount: number;
  sentNoTextMessages: boolean;
  sentOneTextMessage: boolean;
}) {
  const [tutorial1Shown, setTutorial1Shown] = useState(false);
  const [tutorial2Shown, setTutorial2Shown] = useState(false);
  const [temporarilyDisabled, setTemporarilyDisabled] = useState(false);
  const { hasCompletedTutorial, startTutorial } = useTutorials();
  const setChatTutorialVariant = useSetAtom(chatTutorialVariantAtom);
  const currentTutorial = useAtomValue(currentTutorialAtom);
  const introComplete = hasCompletedTutorial(Tutorial.ChatIntro);
  const visibility = getTutorialVisibility({
    channelMessagesLoaded,
    currentTutorial,
    firstMessageTutorialComplete: hasCompletedTutorial(Tutorial.FirstMessageSent),
    introTutorialComplete: introComplete,
    isOwnListing,
    listing,
    loanRequestCount,
    sentNoTextMessages,
    sentOneTextMessage,
    tutorial1Shown,
    tutorial2Shown,
  });
  useEffect(() => {
    if (!visibility.intro) return;
    const timer = setTimeout(() => {
      setChatTutorialVariant(listing?.type === ListingType.request ? 'request' : 'offer');
      startTutorial(Tutorial.ChatIntro);
      setTutorial1Shown(true);
      setTemporarilyDisabled(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [listing?.type, setChatTutorialVariant, startTutorial, visibility.intro]);
  useEffect(() => {
    if (!temporarilyDisabled) return;
    const timer = setTimeout(() => setTemporarilyDisabled(false), 3000);
    return () => clearTimeout(timer);
  }, [temporarilyDisabled]);
  useEffect(() => {
    if (!visibility.firstMessage) return;
    const timer = setTimeout(() => {
      startTutorial(Tutorial.FirstMessageSent);
      setTutorial2Shown(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [startTutorial, visibility.firstMessage]);
  return visibility.intro || (temporarilyDisabled && !introComplete);
}

function useMessageComposer({
  channel,
  markSupportReplyQueued,
  setMessageBeingSent,
}: {
  channel: Channel | undefined;
  markSupportReplyQueued: ReturnType<typeof useSupportReplyWait>['markSupportReplyQueued'];
  setMessageBeingSent: React.Dispatch<React.SetStateAction<MessageType | undefined>>;
}) {
  const toolbar = useChannelToolbar({
    channel,
    listing: channel?.listing,
    setMessageBeingSent,
    onSupportReplyQueued: markSupportReplyQueued,
  });
  const [replyToMessage, setReplyToMessage] = useState<MessageType>();
  const [editingMessage, setEditingMessage] = useState<MessageType>();
  const { mutate: editMessage } = useEditMessage();
  const startEditing = (message: MessageType) => {
    setReplyToMessage(undefined);
    setEditingMessage(message);
    const raw = isUserMessage(message) && 'message' in message ? message.message : '';
    toolbar.setTextContent(typeof raw === 'string' ? raw : '');
  };
  const cancelEditing = () => {
    setEditingMessage(undefined);
    toolbar.setTextContent('');
  };
  const createTextMessage = (message: string) => {
    const trimmed = message.trim();
    if (!trimmed) return;
    if (editingMessage && channel?.channel_url) {
      editMessage(
        { channelUrl: channel.channel_url, messageId: editingMessage.message_id, message: trimmed },
        {
          onSuccess: () => {
            setEditingMessage(undefined);
            toolbar.setTextContent('');
          },
        },
      );
      return;
    }
    toolbar.createTextMessage(trimmed, replyToMessage?.message_id);
    setReplyToMessage(undefined);
  };
  const replyTo = (message: MessageType) => {
    setEditingMessage(undefined);
    setReplyToMessage(message);
  };
  return {
    ...toolbar,
    cancelEditing,
    createTextMessage,
    editingMessage,
    replyTo,
    replyToMessage,
    setReplyToMessage,
    startEditing,
  };
}

function toggleReactionInMap(reactions: Map<string, string[]>, messageId: string, emoji: string) {
  const next = new Map(reactions);
  const current = next.get(messageId) ?? [];
  next.set(messageId, current.includes(emoji) ? current.filter((value) => value !== emoji) : [...current, emoji]);
  return next;
}

function useConversationReactions({
  allMessages,
  channelMessages,
  currentUserId,
  loadedChannelUrl,
}: {
  allMessages: MessageType[];
  channelMessages: MessageType[] | undefined;
  currentUserId: string | undefined;
  loadedChannelUrl: string | undefined;
}) {
  const [showReactionBar, setShowReactionBar] = useState(false);
  const [messageIndex, setMessageIndex] = useState<number | null>(null);
  const { mutate: toggleReaction } = useToggleReaction();
  const [optimisticState, setOptimisticState] = useState({
    source: channelMessages,
    reactions: EMPTY_OPTIMISTIC_REACTIONS,
  });
  const currentState =
    optimisticState.source === channelMessages
      ? optimisticState
      : { source: channelMessages, reactions: EMPTY_OPTIMISTIC_REACTIONS };
  if (optimisticState !== currentState) setOptimisticState(currentState);
  const setOptimisticReactions = (update: React.SetStateAction<Map<string, string[]>>) => {
    setOptimisticState((previous) => ({
      source: channelMessages,
      reactions: resolveStateAction(
        update,
        previous.source === channelMessages ? previous.reactions : EMPTY_OPTIMISTIC_REACTIONS,
      ),
    }));
  };
  const optimisticToggle = (args: { channelUrl: string; messageId: string; emoji: string }) => {
    setOptimisticReactions((previous) => toggleReactionInMap(previous, args.messageId, args.emoji));
    toggleReaction(args, {
      onError: () => setOptimisticReactions((previous) => toggleReactionInMap(previous, args.messageId, args.emoji)),
    });
  };
  const messages = mergeOptimisticReactions(allMessages, currentState.reactions, currentUserId);
  const selectReaction = (emoji: string) => {
    setShowReactionBar(false);
    requestAnimationFrame(() => setMessageIndex(null));
    if (messageIndex === null || !loadedChannelUrl) return;
    const message = messages[messageIndex];
    if (message) optimisticToggle({ channelUrl: loadedChannelUrl, messageId: message.message_id, emoji });
  };
  return {
    isActionActive: showReactionBar && messageIndex !== null,
    messageIndex,
    messages,
    optimisticToggle,
    selectReaction,
    setMessageIndex,
    setShowReactionBar,
    showReactionBar,
  };
}

function useReplyScroll(messages: MessageType[]) {
  const listRef = useRef<FlashOrFlatList<MessageType>>(null);
  const target = useRef<number | null>(null);
  const retries = useRef(0);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const cancel = () => {
    target.current = null;
    retries.current = 0;
    if (timeout.current !== null) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
  };
  useEffect(() => cancel, []);
  const scrollToReply = (messageId: string) => {
    const index = messages.findIndex((message) => message.message_id === messageId);
    if (index < 0 || !listRef.current) return;
    target.current = index;
    retries.current = 0;
    void listRef.current.scrollToIndex({ index, animated: false, viewPosition: 0.5 });
  };
  const onScrollToIndexFailed = (info: { index: number; averageItemLength: number }) => {
    if (!listRef.current || target.current === null) return;
    listRef.current.scrollToOffset({ offset: info.averageItemLength * info.index, animated: false });
    timeout.current = setTimeout(() => {
      retries.current += 1;
      if (retries.current > 20 || target.current === null || !listRef.current) return cancel();
      void listRef.current.scrollToIndex({ index: target.current, animated: false, viewPosition: 0.5 });
    }, 100);
  };
  const handleScrollEnd = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    setShowScrollToBottom(event.nativeEvent.contentOffset.y > 500);
  };
  const onScrollBeginDrag = () => {
    Keyboard.dismiss();
    cancel();
  };
  const scrollToBottom = () => listRef.current?.scrollToOffset({ offset: 0, animated: true });
  return {
    cancel,
    handleScrollEnd,
    listRef,
    onScrollBeginDrag,
    onScrollToIndexFailed,
    scrollToBottom,
    scrollToReply,
    showScrollToBottom,
  };
}

function getIsChannelAdmin(channel: Channel | undefined) {
  return (
    channel?.custom_type === CustomChannelType.Group &&
    'current_user_role' in channel &&
    channel.current_user_role === ChatConversationMemberRole.Admin
  );
}

function useChannelActions({
  channel,
  isLenderUser,
  navigation,
  user,
}: {
  channel: Channel | undefined;
  isLenderUser: boolean;
  navigation: SpecificNavigationProp<'one-channel'>;
  user: ReturnType<typeof useUser>['data'];
}) {
  const loadedChannelUrl = channel?.channel_url;
  const isGroupConversation = channel?.custom_type === CustomChannelType.Group;
  const isAdmin = getIsChannelAdmin(channel);
  const [silenceBottomSheetOpen, setSilenceBottomSheetOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const { mutate: updateChannelSilenced, isPending: isUpdatingChannelSilenced } = useUpdateChannelSilenced();
  const { mutate: leaveGroup, error: leaveGroupError } = useLeaveGroupConversation({
    onSuccess: () => navigateToAllChannels(navigation),
  });
  const { mutate: renameGroup } = useUpdateGroupConversation();
  const onToggleSilenced = (silenced: boolean) => {
    if (!loadedChannelUrl) return;
    if (!silenced) return updateChannelSilenced({ url: loadedChannelUrl, silenced: false });
    setSilenceBottomSheetOpen(true);
  };
  const onSelectSilenceDuration = (durationMinutes?: number) => {
    if (loadedChannelUrl) updateChannelSilenced({ url: loadedChannelUrl, silenced: true, durationMinutes });
  };
  const onLeave = () => {
    if (channel && user) setLeaveModalOpen(true);
  };
  const onRename = () => {
    if (!channel) return;
    setRenameValue(channel.name);
    setRenameModalOpen(true);
  };
  const navigateWithinChat = (screen: 'add-chat-members' | 'chat-members') => {
    if (!loadedChannelUrl) return;
    if (navigation.getState().routeNames.includes(screen)) {
      navigation.navigate(screen, { url: loadedChannelUrl }, { pop: true });
      return;
    }
    navigation.navigate('chat', { screen, params: { url: loadedChannelUrl } }, { pop: true });
  };
  const openHowItWorks = () => {
    if (navigation.getState().routeNames.includes('chat-how-it-works')) {
      navigation.navigate('chat-how-it-works', { isLender: isLenderUser }, { pop: true });
      return;
    }
    navigation.navigate('chat', { screen: 'chat-how-it-works', params: { isLender: isLenderUser } }, { pop: true });
  };
  return {
    channelSilenced: channel ? isChannelSilenced(channel) : false,
    isAdmin,
    isGroupConversation,
    isUpdatingChannelSilenced,
    leaveGroup,
    leaveGroupError,
    leaveModalOpen,
    onAddMembers: () => navigateWithinChat('add-chat-members'),
    onLeave,
    onRename,
    onSeeMembers: () => navigateWithinChat('chat-members'),
    onSelectSilenceDuration,
    onToggleSilenced,
    openHowItWorks,
    renameGroup,
    renameModalOpen,
    renameValue,
    setLeaveModalOpen,
    setRenameModalOpen,
    setRenameValue,
    setSilenceBottomSheetOpen,
    silenceBottomSheetOpen,
  };
}

function ChannelNavigationTitle({
  actions,
  channel,
  linkOffers,
  onMarkAsUnread,
  onTitlePress,
  showBackButton,
  showChevron,
  showContextMenu,
}: {
  actions: ReturnType<typeof useChannelActions>;
  channel: Channel | undefined;
  linkOffers: ReturnType<typeof useLinkOfferController>;
  onMarkAsUnread: () => void;
  onTitlePress: (() => void) | undefined;
  showBackButton?: boolean;
  showChevron: boolean | undefined;
  showContextMenu: boolean;
}) {
  if (!channel) return <View />;
  return (
    <ChannelTitle
      channel={channel}
      embeddedInDrawer={Boolean(showChevron)}
      onMarkAsUnread={onMarkAsUnread}
      onChevronPress={showChevron ? onTitlePress : undefined}
      showBackButton={showBackButton}
      onToggleSilenced={actions.onToggleSilenced}
      showChevron={showChevron}
      silenced={actions.channelSilenced}
      isUpdatingSilence={actions.isUpdatingChannelSilenced}
      isAdmin={actions.isAdmin}
      onAddMembers={actions.isAdmin ? actions.onAddMembers : undefined}
      onLeave={actions.isGroupConversation ? actions.onLeave : undefined}
      onLinkOffer={linkOffers.canLinkOffer ? linkOffers.openModal : undefined}
      onUnlinkOffer={linkOffers.canLinkOffer && linkOffers.linkedOffer ? linkOffers.unlink : undefined}
      onRename={actions.isAdmin ? actions.onRename : undefined}
      showContextMenu={showContextMenu}
    />
  );
}

function ChannelNavigationLeft({
  actions,
  channel,
  linkOffers,
  onMarkAsUnread,
  onTitlePress,
  showChevron,
}: {
  actions: ReturnType<typeof useChannelActions>;
  channel: Channel | undefined;
  linkOffers: ReturnType<typeof useLinkOfferController>;
  onMarkAsUnread: () => void;
  onTitlePress: (() => void) | undefined;
  showChevron: boolean | undefined;
}) {
  const title = (
    <ChannelNavigationTitle
      actions={actions}
      channel={channel}
      linkOffers={linkOffers}
      onMarkAsUnread={onMarkAsUnread}
      onTitlePress={onTitlePress}
      showBackButton={false}
      showChevron={showChevron}
      showContextMenu={false}
    />
  );

  return (
    <Row alignItems="center" flex={1} gap="spacing.general.x2" minWidth={0}>
      <ChannelBackButton />
      {title}
    </Row>
  );
}

function ChannelNavigationRight({
  actions,
  channel,
  linkOffers,
  onMarkAsUnread,
  t,
}: {
  actions: ReturnType<typeof useChannelActions>;
  channel: Channel | undefined;
  linkOffers: ReturnType<typeof useLinkOfferController>;
  onMarkAsUnread: () => void;
  t: TFunction<'one-channel'>;
}) {
  if (!channel) return null;
  return (
    <Row alignItems="center" flexShrink={0}>
      {channel.listing ? (
        <Pressable
          accessibilityLabel={t('howItWorks.title')}
          accessibilityRole="button"
          hitSlop={12}
          onPress={actions.openHowItWorks}
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            paddingVertical: theme.spacing['spacing.general.x1'],
            marginRight: theme.spacing['spacing.general.x2'],
          }}
        >
          <IconHelpCircle
            strokeWidth={ICON_STROKE_WIDTH}
            size={24}
            color={theme.colors['white-label.header.on-header']}
          />
        </Pressable>
      ) : null}
      <ChannelTitleMenuButton
        channel={channel}
        isAdmin={actions.isAdmin}
        onAddMembers={actions.isAdmin ? actions.onAddMembers : undefined}
        onLeave={actions.isGroupConversation ? actions.onLeave : undefined}
        onLinkOffer={linkOffers.canLinkOffer ? linkOffers.openModal : undefined}
        onUnlinkOffer={linkOffers.canLinkOffer && linkOffers.linkedOffer ? linkOffers.unlink : undefined}
        onMarkAsUnread={onMarkAsUnread}
        onRename={actions.isAdmin ? actions.onRename : undefined}
        onToggleSilenced={actions.onToggleSilenced}
        silenced={actions.channelSilenced}
      />
    </Row>
  );
}

const ANDROID_NATIVE_HEADER_HEIGHT = 56;

// Native-stack constrains `headerLeft` as a separate native slot. The channel
// identity is flexible and must live in a real full-width row.
function ChannelNavigationHeader({
  actions,
  channel,
  linkOffers,
  onMarkAsUnread,
  onTitlePress,
  showChevron,
  t,
}: {
  actions: ReturnType<typeof useChannelActions>;
  channel: Channel | undefined;
  linkOffers: ReturnType<typeof useLinkOfferController>;
  onMarkAsUnread: () => void;
  onTitlePress: (() => void) | undefined;
  showChevron: boolean | undefined;
  t: TFunction<'one-channel'>;
}) {
  const safeAreaInsets = useSafeAreaInsets();
  const layout = useWindowDimensions();
  const height =
    Platform.OS === 'android'
      ? ANDROID_NATIVE_HEADER_HEIGHT + safeAreaInsets.top
      : getDefaultHeaderHeight(layout, false, safeAreaInsets.top);

  return (
    <Row
      alignItems="center"
      backgroundColor="white-label.header.bg"
      gap="spacing.general.x2"
      minWidth={0}
      testID="one-channel-navigation-header"
      style={{
        borderBottomColor: theme.colors['surface.disabled'],
        borderBottomWidth: StyleSheet.hairlineWidth,
        height,
        overflow: 'hidden',
        paddingLeft: safeAreaInsets.left + theme.spacing['spacing.general.x2'],
        paddingRight: safeAreaInsets.right + theme.spacing['spacing.general.x2'],
        paddingTop: safeAreaInsets.top,
        width: '100%',
      }}
    >
      <ChannelNavigationLeft
        actions={actions}
        channel={channel}
        linkOffers={linkOffers}
        onMarkAsUnread={onMarkAsUnread}
        onTitlePress={onTitlePress}
        showChevron={showChevron}
      />
      <ChannelNavigationRight
        actions={actions}
        channel={channel}
        linkOffers={linkOffers}
        onMarkAsUnread={onMarkAsUnread}
        t={t}
      />
    </Row>
  );
}

function useChannelNavigation({
  actions,
  channel,
  isLargeUI,
  linkOffers,
  navigation,
  onMarkAsUnread,
  onTitlePress,
  showChevron,
  t,
}: {
  actions: ReturnType<typeof useChannelActions>;
  channel: Channel | undefined;
  isLargeUI: boolean;
  linkOffers: ReturnType<typeof useLinkOfferController>;
  navigation: SpecificNavigationProp<'one-channel'>;
  onMarkAsUnread: () => void;
  onTitlePress: (() => void) | undefined;
  showChevron: boolean | undefined;
  t: TFunction<'one-channel'>;
}) {
  const title = (showContextMenu: boolean) => (
    <ChannelNavigationTitle
      actions={actions}
      channel={channel}
      linkOffers={linkOffers}
      onMarkAsUnread={onMarkAsUnread}
      onTitlePress={onTitlePress}
      showChevron={showChevron}
      showContextMenu={showContextMenu}
    />
  );
  useNavigationOptions(
    navigation,
    isMobileNative
      ? {
          headerBackVisible: false,
          header: () => (
            <ChannelNavigationHeader
              actions={actions}
              channel={channel}
              linkOffers={linkOffers}
              onMarkAsUnread={onMarkAsUnread}
              onTitlePress={onTitlePress}
              showChevron={showChevron}
              t={t}
            />
          ),
        }
      : {
          headerBackVisible: false,
          headerTitle: () => <View />,
          headerLeft: () => (
            <ChannelNavigationLeft
              actions={actions}
              channel={channel}
              linkOffers={linkOffers}
              onMarkAsUnread={onMarkAsUnread}
              onTitlePress={onTitlePress}
              showChevron={showChevron}
            />
          ),
          headerRight: () => (
            <ChannelNavigationRight
              actions={actions}
              channel={channel}
              linkOffers={linkOffers}
              onMarkAsUnread={onMarkAsUnread}
              t={t}
            />
          ),
        },
    Boolean(showChevron) || isLargeUI,
  );
  return title;
}

type ChannelInterlocutor = ReturnType<ReturnType<typeof useGetInterlocutor>>;

interface LoadedChannelScreenProps {
  acceptedLoan: ReturnType<typeof getLoanSummary>['acceptedLoan'];
  actions: ReturnType<typeof useChannelActions>;
  channel: Channel | undefined;
  completedLoan: ReturnType<typeof getLoanSummary>['completedLoan'];
  supersededLoanIds: Set<string>;
  composer: ReturnType<typeof useMessageComposer>;
  inputDisabled: boolean;
  interlocutor: ChannelInterlocutor;
  isAuthor: ReturnType<typeof useIsAuthor>;
  isChannelLoading: boolean;
  isInterlocutorUnavailable: boolean;
  isLargeUI: boolean;
  isLenderUser: boolean;
  isSmallWebUI: boolean;
  isSupportReplyPending: boolean;
  lastViewedMessageSent: MessageType | undefined;
  linkOffers: ReturnType<typeof useLinkOfferController>;
  loadingState: ReturnType<typeof useTopLoadingIndicator>;
  loanFeeModalsState: ReturnType<typeof useLoanFeeModals>;
  navigation: SpecificNavigationProp<'one-channel'>;
  needMatchStatus: React.ComponentProps<typeof Message>['needMatchStatus'];
  promptMessageSent: boolean;
  promptMessageText: string | undefined;
  reactions: ReturnType<typeof useConversationReactions>;
  refetchChannel: ReturnType<typeof useOneChannel>['refetch'];
  renderChannelTitle: (showContextMenu: boolean) => React.ReactNode;
  replyScroll: ReturnType<typeof useReplyScroll>;
  sentNoTextMessages: boolean;
  setMessageBeingSent: React.Dispatch<React.SetStateAction<MessageType | undefined>>;
  setPromptMessageSent: React.Dispatch<React.SetStateAction<boolean>>;
  setToastHidden: React.Dispatch<React.SetStateAction<boolean>>;
  showChevron: boolean | undefined;
  supportBotEnabled: boolean;
  offerHubEnabled: boolean;
  t: TFunction<'one-channel'>;
  toast: string | undefined;
  toastHidden: boolean;
  user: ReturnType<typeof useUser>['data'];
}

function InlineLinkOfferSuggestions({ linkOffers, t }: Pick<LoadedChannelScreenProps, 'linkOffers' | 't'>) {
  if (!linkOffers.canLinkOffer || !linkOffers.showInline) return null;
  const showLoading = linkOffers.query.isFetching && linkOffers.topListings.length === 0;
  return (
    <Column
      gap="spacing.general.x2"
      padding="spacing.general.x3"
      style={{
        backgroundColor: theme.colors['surface.primary-neutral'],
        borderBottomWidth: 3,
        borderBottomColor: theme.colors['border.secondary'],
      }}
    >
      <Row alignItems="center" justifyContent="space-between">
        <Text fontWeight="700">{t('linkOfferInline.title')}</Text>
        <Button
          alignItems="center"
          flex={0}
          justifyContent="center"
          onPress={() => linkOffers.setInlineOpen(false)}
          variant="plain"
        >
          <IconX height={20} width={20} />
        </Button>
      </Row>
      <Text color="text.secondary">{t('linkOfferInline.description', { itemName: linkOffers.itemName })}</Text>
      {showLoading ? (
        <Text color="text.secondary">{t('linkOfferInline.loading')}</Text>
      ) : (
        <Column gap="spacing.general.x2">
          {linkOffers.topListings.map((offer) => (
            <CompactLinkOfferCard
              key={offer.id}
              listing={offer}
              selectedRowsState={[linkOffers.selectedOfferIds, linkOffers.setSelectedOfferIds]}
            />
          ))}
        </Column>
      )}
      {linkOffers.listings.length > linkOffers.topListings.length ? (
        <Button alignSelf="flex-end" flex={0} onPress={linkOffers.openModal} variant="plain">
          <Text color="text.interactive" fontWeight="600" fontSize={12}>
            {t('peopleSearch.viewAll', { ns: 'all-listings' })}
          </Text>
        </Button>
      ) : null}
      <Button
        disabled={!linkOffers.selectedOffer || linkOffers.isLinkingOffer || linkOffers.query.isFetching}
        justifyContent="center"
        minHeight={48}
        onPress={linkOffers.confirm}
        style={{ borderRadius: theme.borderRadii['radius.xl'] }}
        variant="primary"
      >
        <Text color="white" fontWeight="600" textAlign="center">
          {linkOffers.selectedOffer ? t('linkOfferInline.confirm') : t('linkOfferInline.title')}
        </Text>
      </Button>
    </Column>
  );
}

function ChannelContentHeader(props: LoadedChannelScreenProps) {
  const { acceptedLoan, actions, channel, composer, isInterlocutorUnavailable, isLargeUI, isLenderUser } = props;
  const { linkOffers, loadingState, loanFeeModalsState, renderChannelTitle, setToastHidden, showChevron, t } = props;
  const { toast, toastHidden } = props;
  const listing = channel?.listing;
  const showEmbeddedTitle = Boolean(showChevron) || (isLargeUI && Boolean(channel));
  const showUnavailable = channel?.custom_type !== CustomChannelType.Group && isInterlocutorUnavailable;
  return (
    <ChannelHeaderLayout>
      <ShowErrors error={actions.leaveGroupError} skipTitle />
      <ShowErrors error={linkOffers.query.error} skipTitle />
      <ShowErrors error={linkOffers.error} skipTitle />
      <ShowErrors error={composer.createMessage.error} skipTitle />
      {showEmbeddedTitle ? (
        <View
          style={{
            flexShrink: 0,
            width: '100%',
            borderBottomWidth: 1,
            borderBottomColor: theme.colors['surface.disabled'],
          }}
        >
          {renderChannelTitle(true)}
        </View>
      ) : null}
      {showUnavailable ? <UnavailabilityBanner /> : null}
      {listing ? (
        <Banner
          channelURL={channel.channel_url}
          acceptedLoan={acceptedLoan?.id}
          borrowerCreditExhausted={channel?.borrowerCreditExhausted}
          isLender={isLenderUser}
          listing={listing}
          linkedOffer={linkOffers.linkedOffer}
          loanFeeModalsState={loanFeeModalsState}
          onLinkOffer={linkOffers.canLinkOffer && !linkOffers.linkedOffer ? linkOffers.onLinkOffer : undefined}
          showLinkOfferButton={linkOffers.canLinkOffer && !linkOffers.linkedOffer}
          showDatesPicker={() => {
            if (!channel.channel_url) return;
            props.navigation.navigate(
              'loan-dates-picker',
              {
                url: channel.channel_url,
                promptIntroduceYourself: props.sentNoTextMessages,
                goBackTo: 'one-channel',
              },
              { pop: true },
            );
          }}
        />
      ) : null}
      <InlineLinkOfferSuggestions linkOffers={linkOffers} t={t} />
      {loadingState.isLoading ? <TopLoadingIndicator isLoading position="relative" /> : null}
      {toast && !toastHidden ? (
        <Row
          style={{
            backgroundColor: theme.colors['surface.success.subtle'],
            padding: theme.spacing['spacing.general.x4'],
          }}
        >
          <Row
            alignItems="center"
            style={{
              position: 'absolute',
              top: theme.spacing['spacing.general.x1'],
              right: theme.spacing['spacing.general.x1'],
            }}
          >
            <Button hitSlop={12} flex={0} onPress={() => setToastHidden(true)}>
              <IconX strokeWidth={ICON_STROKE_WIDTH} size={22} />
            </Button>
          </Row>
          <Text flex={1} variant="bodyBase">
            {toast}
          </Text>
        </Row>
      ) : null}
    </ChannelHeaderLayout>
  );
}

function ConversationAccessory(props: LoadedChannelScreenProps) {
  const { channel, composer, inputDisabled, isChannelLoading, reactions, sentNoTextMessages } = props;
  if (!(reactions.messages.length >= 0 || composer.editingMessage || composer.replyToMessage)) return null;
  return (
    <ChannelAccessory>
      {composer.editingMessage ? (
        <EditPreview message={composer.editingMessage} onDismiss={composer.cancelEditing} />
      ) : composer.replyToMessage ? (
        <ReplyPreview message={composer.replyToMessage} onDismiss={() => composer.setReplyToMessage(undefined)} />
      ) : null}
      <ChannelToolbar
        channel={channel}
        disabled={inputDisabled}
        isLoading={isChannelLoading}
        listing={channel?.listing}
        sentNoTextMessages={sentNoTextMessages}
        setMessageBeingSent={props.setMessageBeingSent}
        createMessage={composer.createMessage}
        createTextMessage={composer.createTextMessage}
        textContent={composer.textContent}
        setTextContent={composer.setTextContent}
        getMyselfInChannel={composer.getMyselfInChannel}
      />
    </ChannelAccessory>
  );
}

function ConversationListFooter({ actions, channel, interlocutor, navigation, t, user }: LoadedChannelScreenProps) {
  const channelType = channel?.custom_type;
  if (channelType !== CustomChannelType.Group && channelType !== CustomChannelType.Member) return null;
  const seeProfile = () => {
    if (interlocutor?.user_id) navigateToUserProfile(navigation, interlocutor.user_id);
  };
  return (
    <Column
      alignItems="center"
      gap="spacing.general.x4"
      paddingTop="spacing.general.x10"
      paddingHorizontal="spacing.general.x8"
    >
      {channel?.picture ? (
        <PCImage source={{ uri: channel.picture }} style={{ width: 80, height: 80, borderRadius: 40 }} />
      ) : (
        <GroupAvatarStack members={channel?.members ?? []} myUserId={user?.id} size={80} />
      )}
      <Text variant="headerXSmall" textAlign="center" fontWeight="700">
        {channel?.name}
      </Text>
      {channelType === CustomChannelType.Member ? (
        <Button
          variant="secondary"
          disabled={!interlocutor?.user_id}
          onPress={seeProfile}
          style={{ backgroundColor: theme.colors['surface.tertiary-neutral'], borderWidth: 0 }}
        >
          <Row alignItems="center" gap="spacing.general.x1">
            <IconUser strokeWidth={ICON_STROKE_WIDTH} size={16} color={theme.colors['text.primary']} />
            <Text variant="bodyBase">{t('seeProfile')}</Text>
          </Row>
        </Button>
      ) : (
        <Row gap="spacing.general.x2" justifyContent="center" width="100%">
          {actions.isAdmin ? (
            <Button
              variant="secondary"
              onPress={actions.onAddMembers}
              style={{ backgroundColor: theme.colors['surface.tertiary-neutral'], borderWidth: 0 }}
            >
              <Row alignItems="center" gap="spacing.general.x2">
                <IconPlus
                  strokeWidth={ICON_STROKE_WIDTH}
                  size={16}
                  color={theme.colors['text.primary']}
                  style={{
                    borderRadius: theme.borderRadii['radius.sm'],
                    borderWidth: 1,
                    borderColor: theme.colors['text.primary'],
                  }}
                />
                <Text variant="bodyBase">{t('addMembers')}</Text>
              </Row>
            </Button>
          ) : null}
          <Button
            variant="secondary"
            onPress={actions.onSeeMembers}
            style={{ backgroundColor: theme.colors['surface.tertiary-neutral'], borderWidth: 0 }}
          >
            <Row alignItems="center" gap="spacing.general.x1">
              <Clubs height={20} width={20} color={theme.colors['text.primary']} />
              <Text variant="bodyBase">{t('seeMembers')}</Text>
            </Row>
          </Button>
        </Row>
      )}
    </Column>
  );
}

function EmptyConversation({ channel, isChannelLoading, isLenderUser, sentNoTextMessages }: LoadedChannelScreenProps) {
  if (isChannelLoading || !sentNoTextMessages || !channel?.listing) return null;
  return (
    <View style={Platform.OS !== 'android' ? { transform: [{ scaleY: -1 }] } : undefined}>
      <HowItWorksInlineCard isLender={isLenderUser} />
    </View>
  );
}

function ChannelConversation(props: LoadedChannelScreenProps) {
  const {
    channel,
    completedLoan,
    composer,
    inputDisabled,
    interlocutor,
    isAuthor,
    isLenderUser,
    isSmallWebUI,
    isSupportReplyPending,
    lastViewedMessageSent,
    loanFeeModalsState,
    navigation,
    needMatchStatus,
    promptMessageSent,
    promptMessageText,
    reactions,
    refetchChannel,
    replyScroll,
    sentNoTextMessages,
    setPromptMessageSent,
    supportBotEnabled,
    offerHubEnabled,
    t,
    user,
  } = props;
  const channelCustomType = channel?.custom_type;
  const listing = channel?.listing;
  const {
    isActionActive: isConversationActionActive,
    messageIndex,
    messages: allMessagesWithOptimistic,
    optimisticToggle: optimisticToggleReaction,
    selectReaction: handleReactionSelect,
    setMessageIndex,
    setShowReactionBar,
    showReactionBar,
  } = reactions;
  const {
    cancel: cancelScrollToReply,
    handleScrollEnd,
    listRef,
    onScrollBeginDrag,
    onScrollToIndexFailed,
    scrollToBottom,
    scrollToReply,
    showScrollToBottom,
  } = replyScroll;
  const showConvoHeader =
    channelCustomType === CustomChannelType.Group || channelCustomType === CustomChannelType.Member;
  const { createMessage, createTextMessage, replyTo, startEditing } = composer;
  return (
    <View style={[channelLayoutStyles.messageArea, isSmallWebUI ? channelLayoutStyles.webContained : null]}>
      <View
        style={[
          channelLayoutStyles.messageArea,
          isSmallWebUI ? channelLayoutStyles.webContained : null,
          isConversationActionActive ? conversationActionStyles.blurredContent : null,
        ]}
      >
        <ListPage
          style={{ flex: 1, minHeight: 0 }}
          keyboardAvoidingEnabled={Platform.OS === 'web'}
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: showConvoHeader ? 'flex-end' : 'flex-start',
            ...(sentNoTextMessages && listing ? { paddingVertical: theme.spacing['spacing.general.x2'] } : {}),
          }}
          ListEmptyComponent={<EmptyConversation {...props} />}
          accessory={<ConversationAccessory {...props} />}
          data={allMessagesWithOptimistic}
          extraData={offerHubEnabled}
          innerRef={listRef}
          inverted
          key={completedLoan ? completedLoan.id : ''} // Force re-rendering when completedLoan finishes loading
          keyExtractor={(item) => item.message_id}
          onMomentumScrollEnd={handleScrollEnd}
          onScrollBeginDrag={onScrollBeginDrag}
          onScrollEndDrag={handleScrollEnd}
          {...(Platform.OS === 'web' ? { onWheel: cancelScrollToReply } : {})}
          onScrollToIndexFailed={onScrollToIndexFailed}
          ListHeaderComponent={supportBotEnabled && isSupportReplyPending ? <SupportReplyTypingBubble /> : null}
          ListFooterComponent={<ConversationListFooter {...props} />}
          renderItem={({ item, index }) => {
            if (
              item.type === MessageKind.Message &&
              item.custom_type === CustomMessageType.LoanReciprocityPrompt &&
              !offerHubEnabled
            )
              return null;
            const isAuthoredByCurrentUser = isAuthor(item);
            const isAutomaticMessage = item.is_automatic;
            const isWhoMessage =
              channelCustomType === CustomChannelType.Support &&
              Boolean(isAutomaticMessage) &&
              !isAuthoredByCurrentUser;

            const previousMessage = allMessagesWithOptimistic[index + 1];
            const showWhen = !previousMessage || differenceInMinutes(item.created_at, previousMessage.created_at) > 15;

            const showInsuredUser =
              index === allMessagesWithOptimistic.length - 1 &&
              channelCustomType !== CustomChannelType.Support &&
              channelCustomType !== CustomChannelType.Group;

            const shouldHideMessage = item.is_invisible || (isSystemMessage(item) && isAuthoredByCurrentUser); // Only show system messages to the recipient, not the sender

            let messageAvatar: React.ReactNode = null;
            if (item.user?.user_id !== user?.id) {
              if (channelCustomType === CustomChannelType.Group) {
                messageAvatar = (
                  <Pressable
                    onPress={() => {
                      if (!item.user?.user_id) return;
                      navigateToUserProfile(navigation, item.user.user_id);
                    }}
                    hitSlop={12}
                  >
                    <Thumbnail
                      source={item.user?.profile_url}
                      width={28}
                      height={28}
                      style={{ marginTop: theme.spacing['spacing.general.x1'] }}
                    />
                  </Pressable>
                );
              } else if (isWhoMessage) {
                messageAvatar = (
                  <Thumbnail
                    source={images.supportChatMascot}
                    width={28}
                    height={28}
                    style={{ marginTop: theme.spacing['spacing.general.x1'] }}
                  />
                );
              }
            }

            return (
              <View
                style={
                  isConversationActionActive && index === messageIndex && isMobileNative
                    ? {
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.2)',
                      }
                    : undefined
                }
              >
                {promptMessageText && !promptMessageSent && index === 0 ? (
                  <Pressable
                    disabled={createMessage.isPending || !channel || inputDisabled}
                    onPress={() => {
                      createTextMessage(promptMessageText);
                      setPromptMessageSent(true);
                    }}
                    style={{
                      width: '95%',
                      alignSelf: 'center',
                      alignItems: 'center',
                      borderRadius: theme.borderRadii['radius.full'],
                      borderColor: theme.colors['text.tertiary'],
                      borderWidth: 0.5,
                      marginBottom: theme.spacing['spacing.general.x4'],
                      maxWidth: '100%',
                      paddingHorizontal: theme.spacing['spacing.general.x4'],
                      paddingVertical: theme.spacing['spacing.general.x2'],
                      flexDirection: 'row',
                    }}
                  >
                    <View style={{ width: 24, height: 24 }}>
                      <ChatBubble color={theme.colors['text.tertiary']} />
                    </View>
                    <Text
                      numberOfLines={1}
                      style={{
                        color: theme.colors['text.primary'],
                        flexShrink: 1,
                        marginLeft: theme.spacing['spacing.general.x2'],
                        fontSize: 16,
                      }}
                    >
                      {promptMessageText.replace(/\n/g, ' ')}
                    </Text>
                  </Pressable>
                ) : null}
                {showInsuredUser && interlocutor ? (
                  <InsuredUser
                    otherUser={{ insured: interlocutor.insured, profile: { firstName: interlocutor.firstname } }}
                    placement="chat"
                    style={{ marginBottom: theme.spacing['spacing.general.x4'] }}
                  />
                ) : null}

                {showWhen ? (
                  <View
                    style={{
                      alignItems: 'center',
                      marginBottom: theme.spacing['spacing.general.x2'],
                    }}
                  >
                    <Row
                      alignItems="center"
                      gap="spacing.general.x1"
                      style={{
                        borderRadius: theme.borderRadii['radius.full'],
                        paddingHorizontal: theme.spacing['spacing.general.x3'],
                        paddingVertical: theme.spacing['spacing.general.x1'],
                      }}
                    >
                      <Text color="text.tertiary" fontSize={11} variant="bodyBase">
                        {formatMessageSeparator(item.created_at, t)}
                      </Text>
                    </Row>
                  </View>
                ) : null}
                {!shouldHideMessage ? (
                  <Row width="100%" alignItems="flex-start" gap="spacing.general.x1">
                    {messageAvatar}
                    <View style={{ flex: 1 }}>
                      <Message
                        alignSelf={isAuthoredByCurrentUser && !isSystemMessage(item) ? 'flex-end' : 'flex-start'}
                        interlocutor={interlocutor}
                        listRef={listRef}
                        loanFeeModalsState={loanFeeModalsState}
                        message={item}
                        isLoanSuperseded={
                          item.type === MessageKind.Message &&
                          item.custom_type === CustomMessageType.Loan &&
                          props.supersededLoanIds.has(item.loan.id)
                        }
                        needMatchStatus={needMatchStatus}
                        listingId={channel?.listing?.id}
                        setShowReactionBar={setShowReactionBar}
                        setMessageIndex={setMessageIndex}
                        index={index}
                        toggleReaction={optimisticToggleReaction}
                        opacity={isConversationActionActive && index !== messageIndex ? 0.4 : 1}
                        messageIndex={messageIndex}
                        handleReactionSelect={handleReactionSelect}
                        currentUserId={user?.id}
                        onReply={() => replyTo(item)}
                        onEdit={() => startEditing(item)}
                        onScrollToReply={scrollToReply}
                        refetchChannel={refetchChannel}
                        variant={{
                          author: isAuthoredByCurrentUser ? 'current-user' : 'other',
                          loanRole: isLenderUser ? 'lender' : 'borrower',
                          delivery: item.message_id === MESSAGE_ID_LOADING ? 'loading' : 'sent',
                          viewState: lastViewedMessageSent?.message_id === item.message_id ? 'last-viewed' : 'standard',
                          origin: isWhoMessage ? 'support-who' : isAutomaticMessage ? 'automatic' : 'user',
                          actionBar: showReactionBar ? 'visible' : 'hidden',
                          conversation: channelCustomType === CustomChannelType.Group ? 'group' : 'direct',
                          channel: channel && isTransportSupportChannel(channel) ? 'transport-support' : 'standard',
                        }}
                      />
                    </View>
                  </Row>
                ) : null}
              </View>
            );
          }}
          showsVerticalScrollIndicator={false}
        />
        {showScrollToBottom ? (
          <Pressable onPress={scrollToBottom} style={scrollToBottomStyles.button}>
            <IconChevronDown strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['text.primary']} />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function LoadedChannelScreen(props: LoadedChannelScreenProps) {
  const { actions, channel, isSmallWebUI, linkOffers, loanFeeModalsState, t, user } = props;
  const loadedChannelUrl = channel?.channel_url;
  const {
    leaveGroup,
    leaveModalOpen,
    onSelectSilenceDuration,
    renameGroup,
    renameModalOpen,
    renameValue,
    setLeaveModalOpen,
    setRenameModalOpen,
    setRenameValue,
    setSilenceBottomSheetOpen,
    silenceBottomSheetOpen,
  } = actions;
  const {
    canLinkOffer,
    closeModal: closeLinkOfferModal,
    confirm: confirmOfferToLink,
    filters: linkOfferListingsFilters,
    isLinkingOffer,
    listings: myOfferListings,
    query: myOfferListingsQuery,
    searchQuery: linkOfferSearchQuery,
    selectedOffer: selectedOfferToLink,
    selectedOfferIds: selectedOfferToLinkIds,
    setSearchQuery: setLinkOfferSearchQuery,
    setSelectedOfferIds: setSelectedOfferToLinkIds,
    showModal: linkOfferModalOpen,
  } = linkOffers;

  const screenBody = (
    <KeyboardResponsiveView>
      <ChannelContentHeader {...props} />

      <ChannelConversation {...props} />

      <LoanFeeModals state={loanFeeModalsState} />
      <SilenceConversationBottomSheet
        open={silenceBottomSheetOpen}
        onClose={() => setSilenceBottomSheetOpen(false)}
        onSelectDuration={onSelectSilenceDuration}
      />

      {/* Leave group modal */}
      <Modal transparent open={leaveModalOpen} onClose={() => setLeaveModalOpen(false)}>
        <Modal.Card>
          <Modal.Title allowClose onClose={() => setLeaveModalOpen(false)}>
            {t('confirmLeaveTitle')}
          </Modal.Title>
          <Column gap="spacing.general.x12" paddingBottom="spacing.general.x2">
            <Text fontWeight="700" variant="bodyLarge" textAlign="center">
              {t('confirmLeave')}
            </Text>
            <Row gap="spacing.general.x2">
              <Button
                flex={1}
                justifyContent="center"
                minHeight={48}
                onPress={() => setLeaveModalOpen(false)}
                style={{
                  borderRadius: theme.borderRadii['radius.xl'],
                  borderWidth: 1.5,
                  borderColor: theme.colors['text.primary'],
                }}
                variant="plain"
              >
                <Text fontWeight="600" textAlign="center" variant="bodyBase">
                  {t('cancel', { ns: 'common' })}
                </Text>
              </Button>
              <Button
                flex={1}
                justifyContent="center"
                minHeight={48}
                onPress={() => {
                  setLeaveModalOpen(false);
                  if (loadedChannelUrl && user) leaveGroup({ url: loadedChannelUrl, userId: user.id });
                }}
                style={{
                  borderRadius: theme.borderRadii['radius.xl'],
                  backgroundColor: theme.colors['text.primary'],
                }}
                variant="primary"
              >
                <Text color="text.inverse.primary" fontWeight="600" textAlign="center" variant="bodyBase">
                  {t('leaveConversation')}
                </Text>
              </Button>
            </Row>
          </Column>
        </Modal.Card>
      </Modal>

      {/* Link offer modal */}
      <Modal transparent open={linkOfferModalOpen} onClose={closeLinkOfferModal}>
        <Modal.Card>
          <Modal.Title allowClose onClose={closeLinkOfferModal}>
            {t('linkOfferModal.title')}
          </Modal.Title>
          <Column gap="spacing.general.x3" paddingBottom="spacing.general.x2">
            <Text>{t('linkOfferModal.description')}</Text>
            <Column style={{ height: 420 }}>
              <ListingList
                card={CompactLinkOfferCard}
                data={myOfferListings}
                filters={linkOfferListingsFilters}
                isFetchingNextPage={myOfferListingsQuery.isFetchingNextPage}
                isLoading={myOfferListingsQuery.isFetching ? !myOfferListingsQuery.isFetchingNextPage : false}
                listFormat="list"
                ListEmptyComponent={
                  <Text color="text.secondary" marginTop="spacing.general.x3" textAlign="center">
                    {t(linkOfferSearchQuery.trim() ? 'linkOfferInline.empty' : 'linkOfferModal.empty')}
                  </Text>
                }
                metricsLabel="one-channel-link-offer-list"
                numListingsTotal={myOfferListings.length}
                onEndReached={() => {
                  if (myOfferListingsQuery.hasNextPage && !myOfferListingsQuery.isFetchingNextPage) {
                    void myOfferListingsQuery.fetchNextPage();
                  }
                }}
                onRefresh={() => {
                  void myOfferListingsQuery.refetch();
                }}
                onSearchQueryChange={setLinkOfferSearchQuery}
                refreshing={myOfferListingsQuery.isRefetching}
                scrollEnabled
                searchAsYouType={false}
                searchQuery={linkOfferSearchQuery}
                selectedRowsState={[selectedOfferToLinkIds, setSelectedOfferToLinkIds]}
                presentation={{
                  selection: 'enabled',
                  requestBanner: 'hidden',
                  padding: 'none',
                  verticalScrollIndicator: 'visible',
                  searchBar: 'visible',
                  user: 'hidden',
                }}
              />
            </Column>
            <Button
              disabled={!canLinkOffer || !selectedOfferToLink || isLinkingOffer}
              justifyContent="center"
              minHeight={48}
              onPress={confirmOfferToLink}
              style={{
                borderRadius: theme.borderRadii['radius.xl'],
                borderWidth: 1.5,
                borderColor: theme.colors['text.primary'],
              }}
              variant="plain"
            >
              <Text color="text.brand" fontWeight="600" textAlign="center">
                {selectedOfferToLink
                  ? t('linkOfferInline.confirmSelected', { itemName: selectedOfferToLink.item.name })
                  : t('linkOfferInline.confirm')}
              </Text>
            </Button>
          </Column>
        </Modal.Card>
      </Modal>

      {/* Rename group modal */}
      <Modal transparent open={renameModalOpen} onClose={() => setRenameModalOpen(false)}>
        <Modal.Card>
          <Modal.Title allowClose onClose={() => setRenameModalOpen(false)}>
            {t('renameTitle')}
          </Modal.Title>
          <Column gap="spacing.general.x12" paddingBottom="spacing.general.x2">
            <Row
              alignItems="center"
              style={{
                borderWidth: 1,
                borderColor: theme.colors['surface.disabled'],
                borderRadius: theme.borderRadii['radius.lg'],
                paddingHorizontal: theme.spacing['spacing.general.x4'],
                paddingVertical: theme.spacing['spacing.general.x2'],
              }}
            >
              <TextInput
                style={{ flex: 1, fontSize: 16, color: theme.colors['text.primary'] }}
                value={renameValue}
                onChangeText={setRenameValue}
              />
              {renameValue.length > 0 ? (
                <Button onPress={() => setRenameValue('')} variant="plain" hitSlop={8}>
                  <IconX strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.tertiary']} />
                </Button>
              ) : null}
            </Row>
            <Row gap="spacing.general.x2">
              <Button
                flex={1}
                justifyContent="center"
                minHeight={48}
                onPress={() => setRenameModalOpen(false)}
                style={{
                  borderRadius: theme.borderRadii['radius.xl'],
                  borderWidth: 1.5,
                  borderColor: theme.colors['text.primary'],
                }}
                variant="plain"
              >
                <Text fontWeight="600" textAlign="center" variant="bodyBase">
                  {t('cancel', { ns: 'common' })}
                </Text>
              </Button>
              <Button
                flex={1}
                justifyContent="center"
                minHeight={48}
                disabled={!renameValue.trim()}
                onPress={() => {
                  const trimmed = renameValue.trim();
                  if (trimmed && loadedChannelUrl) {
                    renameGroup({ url: loadedChannelUrl, name: trimmed });
                    setRenameModalOpen(false);
                  }
                }}
                style={{
                  borderRadius: theme.borderRadii['radius.xl'],
                  backgroundColor: theme.colors['text.primary'],
                }}
                variant="primary"
              >
                <Text color="text.inverse.primary" fontWeight="600" textAlign="center" variant="bodyBase">
                  {t('save')}
                </Text>
              </Button>
            </Row>
          </Column>
        </Modal.Card>
      </Modal>
    </KeyboardResponsiveView>
  );

  return (
    <View style={[channelLayoutStyles.screen, isSmallWebUI ? channelLayoutStyles.webSmallScreen : null]}>
      {screenBody}
    </View>
  );
}

interface OneChannelScreenInnerProps {
  navigation: SpecificNavigationProp<'one-channel'>;
  route: { params?: SpecificRouteProp<'one-channel'>['params'] };
  showChevron?: boolean;
  onTitlePress?: () => void;
}

const OneChannelScreenComponent = function OneChannelScreenComponent({
  navigation,
  route,
  showChevron,
  onTitlePress,
}: OneChannelScreenInnerProps) {
  const { t } = useTranslation('one-channel');
  const { t: tAllChannels } = useTranslation('all-channels');
  const { isLargeUI, isSmallUI } = useUISize();
  const isSmallWebUI = Platform.OS === 'web' && isSmallUI;
  const isFocused = useIsFocused();
  const setBottomNavigationBarVisible = useSetAtom(bottomNavigationBarVisibleAtom);
  const { data: user, isPending: isUserPending } = useUser();
  const supportBotEnabled = !isUserPending && isFeatureEnabled(DynamicFeatureFlag.SupportBot, user?.featureFlags);
  const offerHubEnabled = canUseOfferHub(user?.featureFlags, isUserPending);
  const { url, messagePrompt, toast } = route.params ?? {};
  const channelUrl = url ?? NO_CHANNEL_SELECTED;

  useFocusEffect(() => {
    setBottomNavigationBarVisible(false);
    return () => setBottomNavigationBarVisible(true);
  });

  const [promptMessageSent, setPromptMessageSent] = useState(false);

  const lifecycle = useChannelLifecycle(channelUrl, isFocused);
  const {
    appState,
    channel,
    loadedChannelUrl,
    markAsUnread: onMarkAsUnread,
    query: channelQuery,
    refetch: refetchChannel,
  } = lifecycle;
  const isChannelLoading = channelQuery.isFetching;
  const channelMessages = channel?.messages;
  const channelCustomType = channel?.custom_type;

  const preparedMessages = usePreparedMessages({
    channel,
    channelUrl,
    currentUserId: user?.id,
    hasCurrentUser: Boolean(user),
    messagePrompt,
  });
  const { allMessages, messages, setMessageBeingSent } = preparedMessages;
  const isAuthor = useIsAuthor();

  const listing = channel?.listing;

  const { acceptedLoan, completedLoan, supersededLoanIds } = getLoanSummary(channelMessages);

  const { data: profile } = useMyProfile();
  const profileUserId = profile?.userId;
  const isLenderUser = isLender(profile?.userId, listing?.guardianId ?? listing?.userId, listing?.type);
  const isOwnListing = listing && listing.userId === profile?.userId;
  const actions = useChannelActions({ channel, isLenderUser, navigation, user });
  const linkOffers = useLinkOfferController({
    acceptedLoan,
    channel,
    isUserPending,
    profileUserId,
    refetchChannel,
    userFeatureFlags: user?.featureFlags,
  });
  const renderChannelTitle = useChannelNavigation({
    actions,
    channel,
    isLargeUI,
    linkOffers,
    navigation,
    onMarkAsUnread,
    onTitlePress,
    showChevron,
    t,
  });

  const messageSummary = getSentMessageSummary(allMessages, profileUserId);
  const { lastViewedMessageSent, loanRequestsSent, sentOneTextMessage } = messageSummary;
  const sentNoTextMessages = channelMessages !== undefined && messageSummary.sentNoTextMessages;
  const inputDisabled = useChannelTutorialState({
    channelMessagesLoaded: channelMessages !== undefined,
    isOwnListing,
    listing,
    loanRequestCount: loanRequestsSent.length,
    sentNoTextMessages,
    sentOneTextMessage,
  });

  const getInterlocutor = useGetInterlocutor();
  const interlocutor = channel ? getInterlocutor(channel) : undefined;
  const isInterlocutorUnavailable = useIsCurrentlyUnavailable(interlocutor?.unavailability);

  const setPageTitle = usePageTitle('one-channel');
  useEffect(
    () =>
      interlocutor &&
      setPageTitle(`title${listing ? 'Listing' : 'User'}`, {
        user: interlocutor.firstname,
        listing: listing?.item.name,
      }),
    [interlocutor, listing, setPageTitle],
  );

  const loadingState = useTopLoadingIndicator(channelQuery);

  const [toastHidden, setToastHidden] = useState(false);

  const loanFeeModalsState = useLoanFeeModals();

  const { isSupportReplyPending, markSupportReplyQueued } = useSupportReplyWait({
    appState,
    channelUrl,
    enabled: supportBotEnabled,
    isFocused,
    isSupportChannel: channelCustomType === CustomChannelType.Support,
    messages,
    refetch: refetchChannel,
  });

  const composer = useMessageComposer({ channel, markSupportReplyQueued, setMessageBeingSent });

  //! Temporarely hardcoded.
  const promptMessageText = getPromptMessageText(user?.id, interlocutor?.firstname);

  const needMatchStatus = channelCustomType === CustomChannelType.Listing ? channel?.needMatch?.status : undefined;

  const reactions = useConversationReactions({
    allMessages,
    channelMessages,
    currentUserId: user?.id,
    loadedChannelUrl,
  });
  const replyScroll = useReplyScroll(reactions.messages);

  if (channelUrl === NO_CHANNEL_SELECTED) {
    if (!isLargeUI) {
      return (
        <>
          <ShowErrors error={actions.leaveGroupError} skipTitle />
          <AllChannelsScreenComponent />
          <LoanFeeModals state={loanFeeModalsState} />
        </>
      );
    }

    return (
      <>
        <Column
          alignItems="center"
          alignSelf="stretch"
          flex={1}
          justifyContent="center"
          minHeight={0}
          minWidth={0}
          padding="spacing.general.x12"
          width="100%"
        >
          <Text textAlign="center" variant="bodyBase">
            {tAllChannels('noChannelSelected')}
          </Text>
        </Column>
        <LoanFeeModals state={loanFeeModalsState} />
      </>
    );
  }

  if ((reactions.messages.length === 0 && loadingState.isLoading) || isUserPending) {
    return (
      <Column flex={1} minHeight={0}>
        <Column flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator size="large" color={theme.colors['text.brand']} />
          <Text variant="bodyBase" marginTop="spacing.general.x4">
            {t('loadingMessages')}
          </Text>
        </Column>
        <LoanFeeModals state={loanFeeModalsState} />
      </Column>
    );
  }

  return (
    <LoadedChannelScreen
      acceptedLoan={acceptedLoan}
      actions={actions}
      channel={channel}
      completedLoan={completedLoan}
      supersededLoanIds={supersededLoanIds}
      composer={composer}
      inputDisabled={inputDisabled}
      interlocutor={interlocutor}
      isAuthor={isAuthor}
      isChannelLoading={isChannelLoading}
      isInterlocutorUnavailable={isInterlocutorUnavailable}
      isLargeUI={isLargeUI}
      isLenderUser={isLenderUser}
      isSmallWebUI={isSmallWebUI}
      isSupportReplyPending={isSupportReplyPending}
      lastViewedMessageSent={lastViewedMessageSent}
      linkOffers={linkOffers}
      loadingState={loadingState}
      loanFeeModalsState={loanFeeModalsState}
      navigation={navigation}
      needMatchStatus={needMatchStatus}
      promptMessageSent={promptMessageSent}
      promptMessageText={promptMessageText}
      reactions={reactions}
      refetchChannel={refetchChannel}
      renderChannelTitle={renderChannelTitle}
      replyScroll={replyScroll}
      sentNoTextMessages={sentNoTextMessages}
      setMessageBeingSent={setMessageBeingSent}
      setPromptMessageSent={setPromptMessageSent}
      setToastHidden={setToastHidden}
      showChevron={showChevron}
      supportBotEnabled={supportBotEnabled}
      offerHubEnabled={offerHubEnabled}
      t={t}
      toast={toast}
      toastHidden={toastHidden}
      user={user}
    />
  );
};

const scrollToBottomStyles = StyleSheet.create({
  button: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors['surface.default'],
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  },
});

const channelLayoutStyles = StyleSheet.create({
  header: {
    flexShrink: 0,
    width: '100%',
  },
  messageArea: {
    flex: 1,
    minHeight: 0,
  },
  screen: {
    flex: 1,
    minHeight: 0,
  },
  webContained: {
    overflow: 'hidden',
  },
  webSmallScreen: {
    ...StyleSheet.absoluteFill,
    overflow: 'hidden',
  },
});

const conversationActionStyles = StyleSheet.create({
  blurredContent: {
    opacity: 0.88,
    ...(Platform.OS === 'android' ? { filter: [{ blur: 8 }] } : null),
  },
});

const OneChannelScreenWithChatLayoutAndMetrics = withOptionalMetrics(
  'OneChannelScreen',
  withErrorBoundary(withDashboardLayout(OneChannelScreenComponent, 'chat')),
);

export function OneChannelScreenWithChatLayout(
  props: React.ComponentProps<typeof OneChannelScreenWithChatLayoutAndMetrics>,
) {
  return <OneChannelScreenWithChatLayoutAndMetrics {...props} />;
}

const OneChannelScreenWithMetrics = withOptionalMetrics(
  'OneChannelScreen',
  withErrorBoundary(OneChannelScreenComponent),
);

export function OneChannelScreen(props: React.ComponentProps<typeof OneChannelScreenWithMetrics>) {
  return <OneChannelScreenWithMetrics {...props} />;
}

export { OneChannelScreenComponent };
